import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { Undo2 } from 'lucide-react';
import { toast } from 'sonner';

export const AssignmentHistoryTab = ({ api }) => {
  const [assignments, setAssignments] = useState([]);
  const [salesPersonNames, setSalesPersonNames] = useState([]);
  const [filterSalesPerson, setFilterSalesPerson] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterSalesPerson !== 'all' ? { sales_person_name: filterSalesPerson } : {};
      const [assignRes, namesRes] = await Promise.all([
        api.get('/marketing-inventory/assignments', { params }),
        api.get('/marketing-inventory/sales-person-names').catch(() => ({ data: { names: [] } })),
      ]);
      setAssignments(assignRes.data.assignments || []);
      setSalesPersonNames(namesRes.data.names || []);
    } catch (err) {
      toast.error('Failed to load assignment history');
    } finally {
      setLoading(false);
    }
  }, [api, filterSalesPerson]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reverseAssignment = async (assignment) => {
    if (!window.confirm(`Reverse assignment of ${assignment.quantity} × "${assignment.item_name}" to ${assignment.sales_person_name}? This returns the quantity to stock.`)) return;
    try {
      await api.delete(`/marketing-inventory/assignments/${assignment.id}`);
      toast.success('Assignment reversed');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reverse assignment');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-lg font-semibold">Assignment History</h2>
        <Select value={filterSalesPerson} onValueChange={setFilterSalesPerson}>
          <SelectTrigger className="w-56"><SelectValue placeholder="All Sales Persons" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sales Persons</SelectItem>
            {salesPersonNames.map((n) => (
              <SelectItem key={n} value={n}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Sales Person</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead>Assigned By</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
              ) : assignments.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No assignments recorded yet</TableCell></TableRow>
              ) : assignments.map((a) => (
                <TableRow key={a.id} data-testid={`mo-assignment-row-${a.id}`}>
                  <TableCell>{a.assigned_date}</TableCell>
                  <TableCell className="font-medium">{a.item_name}</TableCell>
                  <TableCell>{a.sales_person_name}</TableCell>
                  <TableCell className="text-right">{a.quantity}</TableCell>
                  <TableCell className="text-muted-foreground">{a.assigned_by_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{a.note || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => reverseAssignment(a)} title="Reverse this assignment (returns quantity to stock)">
                      <Undo2 className="w-3.5 h-3.5 mr-1" /> Reverse
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
